// @ts-nocheck
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Popper, Paper } from '@mui/material';
import { useStylesPopper } from 'pages/Tenant/AROKMS/KTree/ViewKtreeData';

type HoverPayload = {
  anchor: HTMLElement;
  content: React.ReactNode;
  delayMs?: number; // default 300ms
  placement?: 'right' | 'left' | 'top' | 'bottom';
};

type Ctx = {
  scheduleShow: (p: HoverPayload) => void;
  hide: () => void;
};

const HoverPopperCtx = createContext<Ctx | null>(null);

export function HoverPopperProvider({ children }: { children: React.ReactNode }) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [content, setContent] = useState<React.ReactNode>(null);
  const [arrowRef, setArrowRef] = useState<HTMLElement | null>(null);
  const [placement, setPlacement] = useState<HoverPayload['placement']>('right');

  const timerRef = useRef<number | null>(null);
  const classes = useStylesPopper();

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleShow = useCallback(
    (p: HoverPayload) => {
      clearTimer();
      const { anchor, content, delayMs = 200, placement = 'right' } = p;
      timerRef.current = window.setTimeout(() => {
        setAnchorEl(anchor);
        setContent(content);
        setPlacement(placement);
        timerRef.current = null;
      }, delayMs);
    },
    [clearTimer]
  );

  const hide = useCallback(() => {
    clearTimer();
    setAnchorEl(null);
    setContent(null);
  }, [clearTimer]);

  useEffect(() => () => clearTimer(), [clearTimer]);

  const modifiers = useMemo(
    () => [
      { name: 'flip', options: { altBoundary: true, rootBoundary: 'document', padding: 8 } },
      { name: 'preventOverflow', options: { altAxis: true, tether: true, padding: 8 } },
      { name: 'arrow', options: { element: arrowRef } },
    ],
    [arrowRef]
  );

  return (
    <HoverPopperCtx.Provider value={{ scheduleShow, hide }}>
      {children}

      {anchorEl && content && (
        <Popper
          open
          anchorEl={anchorEl}
          placement={placement}
          disablePortal={false}
          className={classes.popper}
          sx={{ zIndex: 1200 }}
          modifiers={modifiers}
        >
          <span className={classes.arrow} ref={setArrowRef} />
          <Paper
            sx={{
              p: 1.5,
              maxWidth: 300,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              color: '#000',
            }}
            onMouseLeave={hide}
          >
            {content}
          </Paper>
        </Popper>
      )}
    </HoverPopperCtx.Provider>
  );
}

export function useHoverPopper() {
  const ctx = useContext(HoverPopperCtx);
  if (!ctx) throw new Error('useHoverPopper must be used inside HoverPopperProvider');
  return ctx;
}
